import { useQuery } from "@tanstack/react-query";

import { useAuthHeaders } from "../auth/apiClient";

export interface SMSThreadListItem {
  thread_key: string;
  customer_id?: number | null;
  customer_name: string;
  ghosted?: boolean;
  phone_masked: string;
  matched_quote_id?: string | null;
  quote_status?: string | null;
  quote_value: number;
  last_message_at: string;
  last_direction: "inbound" | "outbound-api" | "outbound-reply";
  last_body: string;
  needs_attention: boolean;
  has_stop: boolean;
  message_count: number;
  inbound_count: number;
  outbound_count: number;
  internal_count: number;
}

export interface SMSThreadMessage {
  id: number;
  message_sid?: string | null;
  direction: "inbound" | "outbound" | "internal";
  from_phone_masked: string;
  to_phone_masked: string;
  body: string;
  status?: string | null;
  error_code?: number | null;
  error_message?: string | null;
  occurred_at: string;
  is_stop: boolean;
  matched_quote_id?: string | null;
}

export interface SMSThreadDetail {
  thread_key: string;
  customer_id?: number | null;
  customer_name: string;
  phone_masked: string;
  matched_quote_id?: string | null;
  quote_status?: string | null;
  quote_value: number;
  messages: SMSThreadMessage[];
}

export type SMSPeriodFilter = "recent" | "older" | "all";

export interface SMSThreadFilters {
  period?: SMSPeriodFilter;
  quoteStatus?: "quote_created";
  days?: number;
  from?: string;
  to?: string;
}

export function useSMSThreads(limit = 50, offset = 0, filters: SMSThreadFilters = {}) {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["sms", "threads", limit, offset, filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
        period: filters.period || "recent",
        days: String(filters.days || 14),
      });
      if (filters.from) params.set("from", filters.from);
      if (filters.to) params.set("to", filters.to);
      if (filters.quoteStatus) params.set("quote_status", filters.quoteStatus);
      const res = await fetch(`/api/cockpit/sms/threads?${params.toString()}`, {
        headers,
      });
      if (!res.ok) throw new Error("SMS conversations failed");
      return res.json() as Promise<{
        items: SMSThreadListItem[];
        limit: number;
        offset: number;
        period: SMSPeriodFilter;
        quote_status?: string | null;
        days: number;
        from?: string | null;
        to?: string | null;
      }>;
    },
    staleTime: 60_000,
    refetchInterval: 30_000,
  });
}

export function useSMSThread(threadKey: string | null) {
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: ["sms", "thread", threadKey],
    queryFn: async () => {
      const res = await fetch(`/api/cockpit/sms/threads/${encodeURIComponent(threadKey || "")}`, {
        headers,
      });
      if (!res.ok) throw new Error("SMS conversation failed");
      return res.json() as Promise<SMSThreadDetail>;
    },
    enabled: !!threadKey,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}
